import dagre from '@dagrejs/dagre';
import { Background, Controls, Edge, Handle, MarkerType, Node, NodeProps, Position, ReactFlow, useEdgesState, useNodesState } from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import { useEffect, useMemo } from 'react';

import { AccountCircle, Description, Inventory2 } from '@mui/icons-material';
import { Box, Paper, Stack, Typography, useTheme } from '@mui/material';

import { DomainStoryRevisionModel } from '../../api/api';

type StoryNodeData = {
	label: string;
	description?: string;
	kind: 'actor' | 'workObject';
};

type StoryNode = Node<StoryNodeData, 'story'>;

function DomainStoryNode({ data }: NodeProps<StoryNode>) {
	const Icon = data.kind === 'actor' ? AccountCircle : Inventory2;
	return (
		<Paper variant="outlined" sx={{ width: 190, minHeight: 72, p: 1.25, borderRadius: 1, borderColor: data.kind === 'actor' ? 'primary.main' : 'secondary.main' }}>
			<Handle type="target" position={Position.Left} />
			<Stack direction="row" spacing={1} alignItems="flex-start">
				<Icon color={data.kind === 'actor' ? 'primary' : 'secondary'} fontSize="small" />
				<Box sx={{ minWidth: 0 }}>
					<Typography variant="subtitle2" noWrap title={data.label}>{data.label}</Typography>
					{data.description && <Typography variant="caption" color="text.secondary" sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{data.description}</Typography>}
				</Box>
			</Stack>
			<Handle type="source" position={Position.Right} />
		</Paper>
	);
}

const nodeTypes = { story: DomainStoryNode };

export function buildDomainStoryGraph(revision: DomainStoryRevisionModel): { nodes: StoryNode[]; edges: Edge[] } {
	const graph = new dagre.graphlib.Graph().setDefaultEdgeLabel(() => ({}));
	graph.setGraph({ rankdir: 'LR', nodesep: 60, ranksep: 130, marginx: 30, marginy: 30 });
	const nodes: StoryNode[] = [];

	for (const actor of revision.actors) {
		if (!actor.actorId) continue;
		nodes.push({ id: actor.actorId, type: 'story', position: { x: 0, y: 0 }, data: { label: actor.displayName, description: actor.actorType, kind: 'actor' } });
	}

	for (const workObject of revision.workObjects) {
		if (!workObject.id) continue;
		nodes.push({ id: workObject.id, type: 'story', position: { x: 0, y: 0 }, data: { label: workObject.name, description: workObject.description, kind: 'workObject' } });
	}

	for (const node of nodes) graph.setNode(node.id, { width: 190, height: 78 });

	const nodeIds = new Set(nodes.map((node) => node.id));
	const edges: Edge[] = [];
	for (const step of [...revision.steps].sort((left, right) => left.sequence - right.sequence)) {
		for (const segment of [...step.flowSegments].sort((left, right) => left.position - right.position)) {
			if (!nodeIds.has(segment.from.referenceId) || !nodeIds.has(segment.to.referenceId)) continue;
			const id = `${step.id ?? step.sequence}-${segment.position}-${segment.from.referenceId}-${segment.to.referenceId}`;
			edges.push({
				id,
				source: segment.from.referenceId,
				target: segment.to.referenceId,
				label: segment.activity,
				markerEnd: { type: MarkerType.ArrowClosed },
				style: { strokeWidth: 1.5 },
				labelStyle: { fontSize: 11 },
			});
			graph.setEdge(segment.from.referenceId, segment.to.referenceId);
		}
	}

	dagre.layout(graph);
	for (const node of nodes) {
		const position = graph.node(node.id);
		node.position = { x: position.x - 95, y: position.y - 39 };
	}

	return { nodes, edges };
}

export default function DomainStoryDiagram({ revision }: { revision: DomainStoryRevisionModel }) {
	const theme = useTheme();
	const graph = useMemo(() => buildDomainStoryGraph(revision), [revision]);
	const [nodes, setNodes, onNodesChange] = useNodesState<StoryNode>(graph.nodes);
	const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>(graph.edges);

	useEffect(() => {
		setNodes(graph.nodes);
		setEdges(graph.edges);
	}, [graph, setEdges, setNodes]);

	if (nodes.length === 0) {
		return (
			<Box sx={{ display: 'grid', placeItems: 'center', height: '100%', minHeight: 320, color: 'text.secondary' }}>
				<Stack alignItems="center" spacing={1}><Description /><Typography variant="body2">Add actors and work objects to render the domain story.</Typography></Stack>
			</Box>
		);
	}

	return (
		<Box sx={{ height: '100%', minHeight: 420, bgcolor: 'background.default' }}>
			<ReactFlow
				nodes={nodes}
				edges={edges}
				onNodesChange={onNodesChange}
				onEdgesChange={onEdgesChange}
				nodeTypes={nodeTypes}
				fitView
				fitViewOptions={{ padding: 0.2 }}
				nodesDraggable
				nodesConnectable={false}
				elementsSelectable
				colorMode={theme.palette.mode}
				proOptions={{ hideAttribution: true }}
			>
				<Background gap={20} size={1} />
				<Controls showInteractive={false} />
			</ReactFlow>
		</Box>
	);
}
